import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  applyPromoCode,
  archivePromoCode,
  createPromoCode,
  listPromoCodes,
  listPromoRedemptions,
  listWorkspaces,
  promoStripeReadiness,
  revokePromoRedemption,
  type PromoCodeRow,
  type PromoRedemptionRow,
  type PromoStripeReadinessRow,
  type WorkspaceRow,
} from './api';
import { onlyDate } from './money';

/**
 * Promo codes.
 *
 * The screen has one job the database cannot do for it: make the difference
 * between an OFFER and what a customer GOT impossible to miss. Those are two
 * rows with the same numbers in them right up until you withdraw the offer, at
 * which point one changes and the other must not.
 *
 * So a code reads as a sentence -- "100% off everything for 10 months" -- and
 * the people who took it are listed underneath with their own end dates, which
 * are not the offer's end date and are the thing an admin actually needs when a
 * customer emails asking when they start paying.
 */

const APPLIES_WORDS: Record<string, string> = {
  everything: 'everything',
  plan_fee: 'the plan fee',
  assets: 'asset charges',
};

/** The server's readiness details are fragments; they are shown as sentences. */
function asSentence(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/** "100% off everything for 10 months". The offer, out loud. */
function describe(c: PromoCodeRow): string {
  const pct = Number(c.percent_off);
  const amount = Number.isInteger(pct) ? String(pct) : pct.toFixed(2).replace(/0$/, '');
  const target = APPLIES_WORDS[c.applies_to] ?? c.applies_to;
  const span = c.duration_months == null
    ? 'for as long as they stay'
    : c.duration_months === 1 ? 'for one month' : `for ${c.duration_months} months`;
  return `${amount}% off ${target} ${span}`;
}

export default function PromoCodes({
  onBack,
  toast,
}: {
  onBack: () => void;
  toast: (message: string) => void;
}) {
  const [codes, setCodes] = useState<PromoCodeRow[]>([]);
  const [redemptions, setRedemptions] = useState<PromoRedemptionRow[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const [stripe, setStripe] = useState<PromoStripeReadinessRow[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r, st] = await Promise.all([
        listPromoCodes(),
        listPromoRedemptions(null),
        // Readiness must not be able to stop the screen rendering: it is
        // reporting on a sync that does not exist yet.
        promoStripeReadiness('test').catch(() => [] as PromoStripeReadinessRow[]),
      ]);
      setCodes(c);
      setRedemptions(r);
      setStripe(st);
    } catch (e) {
      toast(`Could not read the promo codes: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    void listWorkspaces().then(setWorkspaces).catch(() => setWorkspaces([]));
  }, []);

  const byCode = useMemo(() => {
    const map = new Map<string, PromoRedemptionRow[]>();
    for (const r of redemptions) {
      const list = map.get(r.promo_code_id) ?? [];
      list.push(r);
      map.set(r.promo_code_id, list);
    }
    return map;
  }, [redemptions]);

  const stripeBy = useMemo(
    () => new Map(stripe.map((r) => [r.promo_code_id, r])),
    [stripe],
  );
  /** Only the disagreements are worth a line. "Not pushed" is every row's state
      until the sync exists, and a warning on every row teaches you to ignore it. */
  const stripeProblems = useMemo(
    () => stripe.filter((r) => r.present && (r.drifted || r.active_mismatch)),
    [stripe],
  );

  /** Every workspace currently on a discount, whichever code it came from. */
  const discounted = useMemo(
    () => new Set(redemptions.filter((r) => r.is_live).map((r) => r.account_id)),
    [redemptions],
  );

  const act = async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      await load();
      toast(label);
    } catch (e) {
      toast(`Refused: ${(e as Error).message}`);
    }
  };

  // --- Making one ------------------------------------------------------------

  const [draft, setDraft] = useState({
    code: '', percent: '100', months: '10',
    appliesTo: 'everything' as 'everything' | 'plan_fee' | 'assets',
    max: '', until: '', description: '',
  });

  const create = async () => {
    const code = draft.code.trim().toUpperCase();
    const percent = Number(draft.percent);
    if (!/^[A-Z0-9][A-Z0-9_-]{2,39}$/.test(code)) {
      return toast('A code is 3–40 characters: letters, numbers, dashes and underscores');
    }
    if (!Number.isFinite(percent) || percent <= 0 || percent > 100) {
      return toast('The discount is a percentage between 1 and 100');
    }
    const months = draft.months.trim() === '' ? null : Number(draft.months);
    if (months !== null && (!Number.isInteger(months) || months < 1)) {
      return toast('Leave the months blank for a discount that never ends, or enter a whole number');
    }
    const max = draft.max.trim() === '' ? null : Number(draft.max);
    if (max !== null && (!Number.isInteger(max) || max < 1)) {
      return toast('Leave the limit blank for no limit, or enter a whole number');
    }
    await act(`${code} created`, async () => {
      await createPromoCode({
        code,
        percentOff: percent,
        durationMonths: months,
        appliesTo: draft.appliesTo,
        maxRedemptions: max,
        validUntil: draft.until.trim() === '' ? null : new Date(`${draft.until}T23:59:59`).toISOString(),
        description: draft.description.trim() || null,
        note: null,
      });
      setDraft({ code: '', percent: '100', months: '10', appliesTo: 'everything', max: '', until: '', description: '' });
      setCreating(false);
    });
  };

  if (loading) return <p className="muted">Loading promo codes…</p>;

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <p className="lede">Discounts you can hand out.</p>
          <p className="sub" style={{ margin: '4px 0 0' }}>
            A code is an offer. Once a customer takes it, what they got is fixed — withdrawing the
            offer, capping it or letting it expire cannot change what somebody already has.
          </p>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn" onClick={onBack}>Back to prices</button>
          <button className="btn primary" onClick={() => setCreating((v) => !v)}>
            {creating ? 'Cancel' : 'New promo code'}
          </button>
        </div>
      </div>

      {creating ? (
        <div className="card" style={{ marginBottom: 18 }}>
          <h2 style={{ marginTop: 0 }}>A new offer</h2>
          <p className="sub">
            The discount, how long it runs and what it comes off cannot be changed once anybody has
            taken it — so this is the part worth reading twice.
          </p>
          <div className="row" style={{ gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label htmlFor="promo-code" style={{ marginTop: 0 }}>Code</label>
              <input
                id="promo-code"
                value={draft.code}
                placeholder="EARLY100"
                style={{ width: 170, textTransform: 'uppercase' }}
                onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
              />
            </div>
            <div>
              <label htmlFor="promo-percent" style={{ marginTop: 0 }}>Discount</label>
              <div className="row" style={{ gap: 6 }}>
                <input
                  id="promo-percent"
                  className="num"
                  value={draft.percent}
                  style={{ width: 70, textAlign: 'right' }}
                  onChange={(e) => setDraft((d) => ({ ...d, percent: e.target.value }))}
                />
                <span className="muted" style={{ fontSize: 13 }}>% off</span>
              </div>
            </div>
            <div>
              <label htmlFor="promo-applies" style={{ marginTop: 0 }}>Coming off</label>
              <select
                id="promo-applies"
                value={draft.appliesTo}
                style={{ padding: '8px 9px' }}
                onChange={(e) => setDraft((d) => ({ ...d, appliesTo: e.target.value as typeof d.appliesTo }))}
              >
                <option value="everything">everything</option>
                <option value="plan_fee">the plan fee only</option>
                <option value="assets">asset charges only</option>
              </select>
            </div>
            <div>
              <label htmlFor="promo-months" style={{ marginTop: 0 }}>For how long</label>
              <div className="row" style={{ gap: 6 }}>
                <input
                  id="promo-months"
                  className="num"
                  value={draft.months}
                  placeholder="never ends"
                  style={{ width: 90, textAlign: 'right' }}
                  onChange={(e) => setDraft((d) => ({ ...d, months: e.target.value }))}
                />
                <span className="muted" style={{ fontSize: 13 }}>months</span>
              </div>
            </div>
            <div>
              <label htmlFor="promo-limit" style={{ marginTop: 0 }}>Limit</label>
              <input
                id="promo-limit"
                className="num"
                value={draft.max}
                placeholder="no limit"
                style={{ width: 100, textAlign: 'right' }}
                onChange={(e) => setDraft((d) => ({ ...d, max: e.target.value }))}
              />
            </div>
            <div>
              <label htmlFor="promo-until" style={{ marginTop: 0 }}>Stop offering it on</label>
              <input
                id="promo-until"
                type="date"
                value={draft.until}
                style={{ width: 170 }}
                onChange={(e) => setDraft((d) => ({ ...d, until: e.target.value }))}
              />
            </div>
          </div>
          <label htmlFor="promo-description">What it is for</label>
          <input
            id="promo-description"
            value={draft.description}
            placeholder="Early access, round one"
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
          />
          <div className="row" style={{ gap: 12, marginTop: 16 }}>
            <button className="btn primary" onClick={() => void create()}>Create it</button>
            <span className="muted" style={{ fontSize: 12.5 }}>
              Creating a code gives it to nobody. You hand it out below.
            </span>
          </div>
        </div>
      ) : null}

      {!codes.length ? (
        <div className="card">
          <p className="sub" style={{ margin: 0 }}>
            No promo codes yet. Make one to give a customer a discount for a fixed number of months.
          </p>
        </div>
      ) : (
        <div className="sheet">
          {codes.map((c) => {
            const mine = byCode.get(c.id) ?? [];
            const open = openId === c.id;
            const gone = c.status === 'archived'
              || (c.valid_until != null && new Date(c.valid_until) <= new Date())
              || (c.max_redemptions != null && c.redemption_count >= c.max_redemptions);
            return (
              <div className="sheet-sec" key={c.id}>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>
                      {c.code}
                      {gone ? (
                        <span className="pill" style={{ marginLeft: 8 }}>
                          {c.status === 'archived' ? 'withdrawn'
                            : c.max_redemptions != null && c.redemption_count >= c.max_redemptions
                              ? 'all taken' : 'expired'}
                        </span>
                      ) : null}
                    </div>
                    <div style={{ fontSize: 14, marginTop: 3 }}>{describe(c)}</div>
                    <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                      {c.max_redemptions != null
                        ? `${c.redemption_count} of ${c.max_redemptions} taken`
                        : c.redemption_count === 0
                          ? 'Nobody has taken it'
                          : `${c.redemption_count} ${c.redemption_count === 1 ? 'customer has' : 'customers have'} taken it`}
                      {c.live_count !== c.redemption_count ? ` · ${c.live_count} still running` : ''}
                      {c.valid_until ? ` · offered until ${onlyDate(c.valid_until)}` : ''}
                      {c.description ? ` · ${c.description}` : ''}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 8, flexShrink: 0 }}>
                    <button className="btn" onClick={() => setOpenId(open ? null : c.id)}>
                      {open ? 'Close' : mine.length ? `Who has it (${mine.length})` : 'Give it out'}
                    </button>
                    {c.status === 'active' ? (
                      <button
                        className="btn"
                        onClick={() => {
                          if (!window.confirm(
                            `Stop offering ${c.code}?\n\nNobody new can take it. Everyone who already `
                            + 'has it keeps it, on exactly the terms they took.',
                          )) return;
                          return act(`${c.code} is no longer offered`, () => archivePromoCode(c.id));
                        }}
                      >
                        Stop offering it
                      </button>
                    ) : null}
                  </div>
                </div>

                {(() => {
                  const st = stripeBy.get(c.id);
                  if (!st || !st.present || (!st.drifted && !st.active_mismatch)) return null;
                  return (
                    <div className="note-bar warn" style={{ marginTop: 12 }}>
                      <div><b>Stripe disagrees.</b>{asSentence(st.detail)}</div>
                    </div>
                  );
                })()}

                {open ? (
                  <div style={{ marginTop: 16 }}>
                    <GiveItOut
                      code={c}
                      workspaces={workspaces}
                      discounted={discounted}
                      onGive={(accountId, name) =>
                        act(`${name} is now on ${c.code}`, () => applyPromoCode(c.id, accountId))}
                    />

                    {mine.length ? (
                      <div className="matrix-scroll" style={{ marginTop: 14 }}>
                        <table className="matrix">
                          <thead>
                            <tr>
                              <th>Customer</th><th>Took it</th><th>Pays again from</th>
                              <th>State</th><th />
                            </tr>
                          </thead>
                          <tbody>
                            {mine.map((r) => (
                              <tr key={r.id}>
                                <td className="feat-name">{r.workspace_name ?? r.account_id.slice(0, 8)}</td>
                                <td>{onlyDate(r.redeemed_at)}</td>
                                <td>
                                  {r.ends_at ? onlyDate(r.ends_at) : <span className="muted">never</span>}
                                </td>
                                <td>
                                  {r.revoked_at ? (
                                    <>
                                      <span className="muted">taken away</span>
                                      {r.revoked_reason ? (
                                        <div className="muted" style={{ fontSize: 11 }}>{r.revoked_reason}</div>
                                      ) : null}
                                    </>
                                  ) : r.is_live ? (
                                    <span style={{ color: 'var(--on)', fontWeight: 600 }}>running</span>
                                  ) : (
                                    <span className="muted">finished</span>
                                  )}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {r.is_live ? (
                                    <button
                                      className="btn"
                                      style={{ padding: '4px 8px', fontSize: 12 }}
                                      onClick={() => {
                                        const why = window.prompt(
                                          `Take ${c.code} away from ${r.workspace_name ?? 'this workspace'}?\n\n`
                                          + 'They go back to paying full price. Why?');
                                        if (!why || !why.trim()) return;
                                        return act('Discount taken away', () =>
                                          revokePromoRedemption(r.id, why.trim()));
                                      }}
                                    >
                                      Take it away
                                    </button>
                                  ) : null}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="hint" style={{ marginTop: 12 }}>Nobody has this code yet.</p>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {codes.length ? (
        <div className="card" style={{ marginTop: 18 }}>
          <h2 style={{ marginTop: 0 }}>Stripe</h2>
          <p className="sub" style={{ marginBottom: 10 }}>
            Stripe holds a copy of each offer as a coupon. This database is the source of truth; the
            sync that creates the coupons and writes their ids back is not built yet, so a discount
            here does not change an invoice.
          </p>
          <div className="line">
            <span className="what">
              Offers Stripe carries
              <span className="note">test mode</span>
            </span>
            <span className="lead" />
            <span className="fig" style={{ fontWeight: 400 }}>
              <span className="muted">
                {stripe.filter((r) => r.present).length} of {codes.length}
              </span>
            </span>
          </div>
          {stripe.some((r) => r.blocked_on_prices) ? (
            <div className="line">
              <span className="what">
                Waiting on the price sync
                <span className="note">
                  a code aimed at the plan fee or at assets restricts to Stripe Products, and those
                  do not exist until the prices are pushed
                </span>
              </span>
              <span className="lead" />
              <span className="fig" style={{ fontWeight: 400 }}>
                <span className="muted">{stripe.filter((r) => r.blocked_on_prices).length}</span>
              </span>
            </div>
          ) : null}
          {stripeProblems.length ? (
            <div className="note-bar warn" style={{ marginTop: 12 }}>
              <div>
                <b>
                  {stripeProblems.length} offer{stripeProblems.length === 1 ? '' : 's'} Stripe
                  carries {stripeProblems.length === 1 ? 'no longer matches' : 'no longer match'} what
                  is here.
                </b>
                {stripeProblems.map((r) => `${r.code}: ${asSentence(r.detail)}`).join(' · ')}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/**
 * Handing a code to one customer. A workspace already on a discount is listed
 * but not selectable, with the reason said rather than implied -- a Stripe
 * subscription carries one discount, so the server would refuse it anyway, and
 * a refusal after the click teaches nothing.
 */
function GiveItOut({
  code,
  workspaces,
  discounted,
  onGive,
}: {
  code: PromoCodeRow;
  workspaces: WorkspaceRow[];
  discounted: Set<string>;
  onGive: (accountId: string, name: string) => void;
}) {
  const [picked, setPicked] = useState('');

  const closed = code.status !== 'active'
    || (code.valid_until != null && new Date(code.valid_until) <= new Date())
    || (code.max_redemptions != null && code.redemption_count >= code.max_redemptions);

  if (closed) {
    return (
      <p className="hint" style={{ margin: 0 }}>
        This code is no longer being offered, so it cannot be given to anybody new. Everyone below
        keeps what they took.
      </p>
    );
  }

  const chosen = workspaces.find((w) => w.account_id === picked);

  return (
    <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: 13.5 }}>Give {code.code} to</span>
      <select
        value={picked}
        aria-label={`Customer to give ${code.code} to`}
        onChange={(e) => setPicked(e.target.value)}
        style={{ padding: '7px 9px', maxWidth: 320 }}
      >
        <option value="">choose a customer…</option>
        {workspaces.map((w) => (
          <option key={w.account_id} value={w.account_id} disabled={discounted.has(w.account_id)}>
            {w.name || w.account_id.slice(0, 8)}
            {discounted.has(w.account_id) ? ' — already on a discount' : ''}
          </option>
        ))}
      </select>
      <button
        className="btn"
        disabled={!picked}
        onClick={() => chosen && onGive(chosen.account_id, chosen.name || 'Workspace')}
      >
        Give it
      </button>
    </div>
  );
}
